/* eslint-disable max-lines */
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { Button } from "@cmsgov/ds-medicare-gov";
import { AppContext } from "../app/store";
import {
  getBeneSessionInfo as getSessionInfo,
  hasGlobalSession,
  logError,
  msToMinsAndSeconds,
  msToMinutes,
  useCypressEnv,
  useTranslate,
} from "../helpers";
import { sendKeepAlive } from "../api";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router";
import routes from "../app/routes";
import { ActionType } from "../@types";
import { ApiError } from "../helpers/errors";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
} from "../app/contexts/Analytics/types";
import { config } from "../config";
import { MandatoryDialog } from "../components";
import {
  IdleTimeoutLogoutParams,
  idleTimeoutLogout,
} from "../helpers/sessionHelpers";

export const IdleTimeout: FunctionComponent = ({ children }) => {
  enum IdleTimeoutActivityState {
    ACTIVE = "active",
    IDLE = "idle",
    LOGGED_OUT = "logged_out",
  }

  const {
    state: { beneficiary },
    state,
    dispatch,
  } = useContext(AppContext);
  const location = useLocation();
  const t = useTranslate();
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const cypressEnv = useCypressEnv();
  const disableIdleTimout = cypressEnv.DISABLE_IDLE_TIMEOUT;

  // Constants
  const ONE_MINUTE_MS = 1000 * 60;
  // const ACTIVITY_THROTTLE_MS = ONE_MINUTE_MS;
  const EVENT_LISTENER_THROTTLE_MS = ONE_MINUTE_MS / 2;
  const FIVE_MINUTE_MS = ONE_MINUTE_MS * 5;
  const CSR_IDLE_TIMEOUT_MS = ONE_MINUTE_MS * 15;
  const BASE_FORCE_LOGOUT_TIMEOUT_MS = config.FORCE_LOGOUT_TIMEOUT_MINUTES
    ? config.FORCE_LOGOUT_TIMEOUT_MINUTES * 60 * 1000
    : FIVE_MINUTE_MS;
  const FORCE_LOGOUT_TIMEOUT_MS = beneficiary?.csr_id
    ? FIVE_MINUTE_MS
    : BASE_FORCE_LOGOUT_TIMEOUT_MS;

  // State
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutModalShown, setForceLogoutModalShown] = useState(false);
  const [timeouts, setTimeouts] = useState<{
    idleTimeoutMs: number;
    forceLogoutTimeoutMs: number;
  }>({
    // Set the initial value to one minute to prevent pop-up from showing immediately
    idleTimeoutMs: ONE_MINUTE_MS,
    forceLogoutTimeoutMs: 0,
  });
  const [activityState, setActivityState] =
    useState<IdleTimeoutActivityState>();

  // Refs
  const hasSessionWithBeneficiary = hasGlobalSession(state);
  const isLoggedInRef = useRef(hasSessionWithBeneficiary);
  const logoutTimeoutRef = useRef<NodeJS.Timeout>();
  const logoutTimeoutStartTimeRef = useRef(Date.now());
  const cachedTimeoutsRef = useRef({
    idleTimeoutMs: timeouts.idleTimeoutMs,
    forceLogoutTimeoutMs: timeouts.forceLogoutTimeoutMs,
    loggedOut: isLoggedInRef.current,
  });
  const timerStartedRef = useRef(false);
  const useCachedTimeoutValues = useRef(false);
  const getSessionInfoBeforeLogoutRef = useRef(false);

  // Timer definition
  const {
    pause: pauseTimer,
    start: startTimer,
    isIdle,
    getLastActiveTime,
    getElapsedTime,
    getRemainingTime,
  } = useIdleTimer({
    timeout: timeouts.idleTimeoutMs,
    onIdle: () => {
      if (isLoggedInRef.current) {
        console.debug("🦊 onIdle called");
        setActivityState(IdleTimeoutActivityState.IDLE);
      }
    },
    onAction: (e: Event | undefined) => {
      if (isLoggedInRef.current) {
        console.debug("🐸 onAction called", e?.type);
        dispatch({
          type: ActionType.UPDATE_LAST_ACTIVITY,
          payload: getLastActiveTime(),
        });
        keepSessionAlive();
      } else {
        if (
          activityState &&
          activityState !== IdleTimeoutActivityState.LOGGED_OUT
        ) {
          closeSession();
        }
      }
    },
    eventsThrottle: EVENT_LISTENER_THROTTLE_MS,
  });

  // Values to pass for logging
  const idleTimeoutLogoutParams: Omit<
    IdleTimeoutLogoutParams,
    "clientExpired"
  > = {
    beneficiary,
    dispatch,
    forceLogoutModalShown,
    getLastActiveTime,
    isLoggedInRef,
    state,
    timeouts,
  };

  // Hooks
  // Start by pausing the timer if the user isn't logged in
  useEffect(() => {
    if (!isLoggedInRef.current) {
      pauseTimer();
    }
  }, [pauseTimer]);

  // This `useEffect` kicks off the IdleTimeout handler
  useEffect(() => {
    if (disableIdleTimout) return;

    isLoggedInRef.current = hasSessionWithBeneficiary;

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    // The timer will be started at some point after activity state is set to "active"
    if (hasSessionWithBeneficiary && !timerStartedRef.current) {
      setActivityState(IdleTimeoutActivityState.ACTIVE);
    }
  }, [hasSessionWithBeneficiary, disableIdleTimout]);

  useEffect(() => {
    if (!isLoggedInRef.current || timeouts.idleTimeoutMs === 0) {
      return;
    }

    setShowForceLogoutModal(false);

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }
  }, [timeouts.idleTimeoutMs]);

  useEffect(() => {
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    if (activityState === IdleTimeoutActivityState.IDLE) {
      setShowForceLogoutModal(true);

      pauseTimer();

      logoutTimeoutStartTimeRef.current = Date.now();
      logoutTimeoutRef.current = setTimeout(() => {
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.SYSTEM_END_IDLE_SESSION,
          },
        });

        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_label: "mct_plan_finder_system_end_idle_session",
            event_action: "idle session - system ended session",
            other_props: {
              beneficiary_key: beneficiary?.meta_data.beneficiary_key,
              csr_id: beneficiary?.csr_id,
            },
          },
        });

        setActivityState(IdleTimeoutActivityState.LOGGED_OUT);
      }, timeouts.forceLogoutTimeoutMs);
    }
  }, [timeouts.forceLogoutTimeoutMs]);

  useEffect(() => {
    if (showForceLogoutModal) {
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.SHOW_IDLE_SESSION_MODAL,
        },
      });

      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_label: "mct_plan_finder_show_idle_session_modal",
          event_action: "idle session - modal impression",
          other_props: {
            beneficiary_key: beneficiary?.meta_data.beneficiary_key,
            csr_id: beneficiary?.csr_id,
          },
        },
      });

      setForceLogoutModalShown(true);
    }
  }, [showForceLogoutModal]);

  /**
   * Watch activity state changes and handle with appropriate function
   */
  useEffect(() => {
    console.debug(
      `🛎️ activityState change to ${activityState}, isLoggedInRef.current: ${isLoggedInRef.current}`
    );
    if (isLoggedInRef.current && !isMbpLandingRoute && !disableIdleTimout) {
      // mbpLandingRoute always redirects, so don't try this on that route
      if (activityState === IdleTimeoutActivityState.ACTIVE) {
        startIdleTimer();
      } else if (activityState === IdleTimeoutActivityState.IDLE) {
        startForceLogoutTimer();
      } else if (activityState === IdleTimeoutActivityState.LOGGED_OUT) {
        getSessionInfoBeforeLogoutRef.current = true;
        closeSession();
      }
    }
  }, [activityState, isMbpLandingRoute, disableIdleTimout]);

  // Methods
  const keepSessionAlive = async () => {
    if (isLoggedInRef.current) {
      if (location.pathname !== routes.logout) {
        try {
          console.debug(`🦄 keepSessionAlive requesting to update session`);
          await sendKeepAlive();
        } catch (err) {
          const e = err as ApiError;
          const message = e.message || "";
          logError(`Error calling /beneinfo/keep-alive: ${message}`, e);
          closeSession();
        }
      }
    } else {
      closeSession();
    }
  };

  const getBeneSessionInfo = async () => {
    const info = await getSessionInfo(FORCE_LOGOUT_TIMEOUT_MS);
    cachedTimeoutsRef.current = {
      idleTimeoutMs: info.idleTimeoutMs,
      forceLogoutTimeoutMs: info.forceLogoutTimeoutMs,
      loggedOut: info.loggedOut,
    };
    return info;
  };

  const startIdleTimer = async () => {
    if (!beneficiary?.csr_id) {
      const { idleTimeoutMs, forceLogoutTimeoutMs, loggedOut } =
        useCachedTimeoutValues.current
          ? cachedTimeoutsRef.current
          : await getBeneSessionInfo();
      useCachedTimeoutValues.current = false;

      if (loggedOut) {
        return idleTimeoutLogout(idleTimeoutLogoutParams);
      } else if (idleTimeoutMs > 0) {
        setTimeouts({
          idleTimeoutMs,
          forceLogoutTimeoutMs,
        });
      } else if (forceLogoutTimeoutMs > 0) {
        return setActivityState(IdleTimeoutActivityState.IDLE);
      }
    } else {
      setTimeouts({ ...timeouts, idleTimeoutMs: CSR_IDLE_TIMEOUT_MS });
    }
    if (!timerStartedRef.current) {
      console.debug(`☣️ starting timer`);
      startTimer();
      timerStartedRef.current = true;
    }
  };

  const startForceLogoutTimer = async () => {
    if (!beneficiary?.csr_id) {
      const { idleTimeoutMs, forceLogoutTimeoutMs, loggedOut } =
        await getBeneSessionInfo();
      console.debug(
        `🐝 startForceLogoutTimer called - idleTimeoutMs: ${idleTimeoutMs}, forceLogoutTimeoutMs: ${forceLogoutTimeoutMs}, loggedOut: ${loggedOut}`
      );

      if (loggedOut) {
        return idleTimeoutLogout(idleTimeoutLogoutParams);
      } else if (idleTimeoutMs > 0) {
        useCachedTimeoutValues.current = true;
        return setActivityState(IdleTimeoutActivityState.ACTIVE);
      } else if (forceLogoutTimeoutMs > 0) {
        setTimeouts({ ...timeouts, forceLogoutTimeoutMs });
      }
    } else {
      setTimeouts({
        ...timeouts,
        forceLogoutTimeoutMs: FORCE_LOGOUT_TIMEOUT_MS,
      });
    }
    if (!timerStartedRef.current) {
      console.debug(`☣️ starting timer`);
      startTimer();
      timerStartedRef.current = true;
    }
  };

  /**
   * If logout should display the toast "You have successfully logged out" instead
   * of "Your session has expired," pass the optional `{clientExpired: false}`
   */
  const closeSession = async ({ clientExpired = true } = {}) => {
    if (!beneficiary?.csr_id && getSessionInfoBeforeLogoutRef.current) {
      const { idleTimeoutMs, forceLogoutTimeoutMs } =
        await getBeneSessionInfo();

      if (idleTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.ACTIVE);

        return;
      } else if (forceLogoutTimeoutMs > 0) {
        setActivityState(IdleTimeoutActivityState.IDLE);

        return;
      }
    }

    getSessionInfoBeforeLogoutRef.current = false;

    idleTimeoutLogout({ ...idleTimeoutLogoutParams, clientExpired });
  };

  const extendSessionAndContinue = async () => {
    await keepSessionAlive();

    setShowForceLogoutModal(false);

    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.CONTINUE_IDLE_SESSION,
      },
    });

    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_label: "mct_plan_finder_continue_idle_session",
        event_action: "idle session - continue session",
        other_props: {
          beneficiary_key: beneficiary?.meta_data.beneficiary_key,
          csr_id: beneficiary?.csr_id,
        },
      },
    });

    setActivityState(IdleTimeoutActivityState.ACTIVE);
  };

  // For Testing
  const [remaining, setRemaining] = useState(0);
  const [lastActive, setLastActive] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [timeToLogout, setTimeToLogout] = useState<number | null>(null);
  const forceLogoutTimeoutInfoRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!config.FORCE_LOGOUT_TIMEOUT_INFO) {
      return;
    }
    forceLogoutTimeoutInfoRef.current &&
      clearInterval(forceLogoutTimeoutInfoRef.current);
    forceLogoutTimeoutInfoRef.current = isLoggedInRef.current
      ? setInterval(() => {
          const promptTimeLeft = showForceLogoutModal
            ? timeouts.forceLogoutTimeoutMs -
              (Date.now() - logoutTimeoutStartTimeRef.current)
            : null;
          setRemaining(getRemainingTime());
          setElapsed(getElapsedTime());
          setLastActive(getLastActiveTime());
          setTimeToLogout(promptTimeLeft);
        }, 1000)
      : (1 as unknown as NodeJS.Timeout);
    return () => {
      isLoggedInRef.current &&
        forceLogoutTimeoutInfoRef.current &&
        clearInterval(forceLogoutTimeoutInfoRef.current);
    };
  }, [showForceLogoutModal, activityState]);

  return (
    <>
      {/* For debugging purposes, only renders when configured in the build */}
      {config.FORCE_LOGOUT_TIMEOUT_INFO && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 300,
            padding: "20px",
            backgroundColor: "white",
            opacity: 0.9,
            border: "1px solid grey",
            borderRadius: "10px",
            zIndex: 9996,
          }}
        >
          <div>isIdle: {`${isIdle()}`}</div>
          <div>IdleTimoutActivityState: {activityState}</div>
          <div>isLoggedIn (timer started): {`${isLoggedInRef.current}`}</div>
          {/* Remaining time is only to prompt if "promptBeforeIdle" prop isn't used */}
          <div>
            Remaining time to prompt: {`${msToMinsAndSeconds(remaining)}`}
          </div>
          {/* Time to logout if "promptBeforeIdle" prop isn't used */}
          {timeToLogout && timeToLogout > 0 && (
            <div>Time to logout: {`${msToMinsAndSeconds(timeToLogout)}`}</div>
          )}
          <div>Last active time: {lastActive?.toLocaleTimeString()}</div>
          <div>Time since mounted: {`${msToMinsAndSeconds(elapsed)}`}</div>
        </div>
      )}
      {/* The "Your session will expire" prompt */}
      {showForceLogoutModal && (
        <MandatoryDialog
          heading={t("user_activity.title")}
          onExit={extendSessionAndContinue}
          actions={[
            <Button
              variation="solid"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.PRIMARY,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.continue_session"),
                    },
                  },
                });
                // @TODO - because this is a custom implementation, we need to call
                // `extendSessionAndContinue` here and in the `onExit` prop callback
                extendSessionAndContinue();
              }}
              key="primary"
            >
              {t("user_activity.continue_session")}
            </Button>,
            <Button
              variation="ghost"
              key="cancel"
              onClick={(): void => {
                dispatch({
                  type: AnalyticsActionType.SEND_GA4_EVENT,
                  settings: {
                    event_name: Ga4Event.USER_END_IDLE_SESSION,
                  },
                });

                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_label: "mct_plan_finder_user_end_idle_session",
                    event_action: "idle session - user ended session",
                    other_props: {
                      beneficiary_key: beneficiary?.meta_data.beneficiary_key,
                      csr_id: beneficiary?.csr_id,
                    },
                  },
                });

                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.logout"),
                    },
                  },
                });

                idleTimeoutLogout(idleTimeoutLogoutParams);
              }}
            >
              {t("user_activity.logout")}
            </Button>,
          ]}
        >
          <FormattedMessage
            id="idle_timeout.inactive"
            values={{
              minutes: Math.ceil(msToMinutes(timeouts.forceLogoutTimeoutMs)),
            }}
          />
        </MandatoryDialog>
      )}
      {children}
    </>
  );
};
